import React, {
  createContext,
  useContext,
  useState,
  FunctionComponent,
  useEffect,
  useCallback,
} from 'react';
import { MinMaxDomain } from './getMinMaxSeries';

interface MinMaxSyncContextProps {
  store?: (seriesLength: number, xDomain: MinMaxDomain) => void;
  xDomain: MinMaxDomain;
  yDomain: number[];
}

type MinMaxSyncState = Omit<MinMaxSyncContextProps, 'store'>;

export const MinMaxSyncContext = createContext<null | MinMaxSyncContextProps>(null);

export const useMinMaxSync = (
  seriesLength: number,
  xDomain: MinMaxDomain,
): Omit<MinMaxSyncContextProps, 'store'> => {
  const sync = useContext(MinMaxSyncContext);
  const store = sync && sync.store;
  const [data, setData] = useState<MinMaxSyncState>({
    xDomain: [0, 1],
    yDomain: [0, 1, 2, 3, 4],
  });

  useEffect(() => {
    if (store) {
      store(seriesLength, xDomain);
    } else {
      setData({
        xDomain,
        yDomain: Array.from({ length: seriesLength })
          .map((_, i) => i)
          .reverse(),
      });
    }
  }, [seriesLength, store, xDomain]);

  return sync || data;
};

const MinMaxSync: FunctionComponent = (props) => {
  const [dataSynced, setData] = useState<MinMaxSyncState>({
    xDomain: [0, 1],
    yDomain: [0, 1, 2, 3, 4],
  });

  const store = useCallback((seriesLength: number, xDomain: MinMaxDomain) => {
    setData((data) => {
      const nextSeriesLength =
        seriesLength > data.yDomain.length ? seriesLength : data.yDomain.length;
      const nextXLower = xDomain[0] < data.xDomain[0] ? xDomain[0] : data.xDomain[0];
      const nextXUpper = xDomain[1] > data.xDomain[1] ? xDomain[1] : data.xDomain[1];
      const nextXDomain =
        data.xDomain[0] === xDomain[0] && data.xDomain[1] === xDomain[1]
          ? data.xDomain
          : [nextXLower, nextXUpper];

      if (nextSeriesLength === data.yDomain.length && nextXDomain === data.xDomain) {
        return data;
      }

      return {
        xDomain: nextXDomain as MinMaxDomain,
        yDomain: Array.from({ length: nextSeriesLength })
          .map((_, i) => i)
          .reverse(),
      };
    });
  }, []);

  return <MinMaxSyncContext.Provider {...props} value={{ ...dataSynced, store }} />;
};

export default MinMaxSync;
